import type { AdminShopCatalog } from "../../../../api/shop";
import { Alert, ChipGroup, EmptyState, Field } from "../../../../ui";
import { Combinations, MAX_SKUS, TOO_MANY_COMBINATIONS } from "../combinations";
import type { ProductForm } from "../editorModel";

export type VariantsSectionProps = {
  form: ProductForm;
  catalog: AdminShopCatalog;
  combos: Combinations;
  onChange: (values: Partial<ProductForm>) => void;
  errorAt: (id: string) => string | null;
};

const byOrder = <T extends { displayOrder: number }>(list: T[]) => [...list].sort((a, b) => a.displayOrder - b.displayOrder);

/** Editor §3 (with variants only): values per option type and sizes as chips, in catalog order. */
export function VariantsSection({ form, catalog, combos, onChange, errorAt }: VariantsSectionProps) {
  const types = byOrder(catalog.optionTypes).filter((t) => t.values.length > 0);
  const sizes = byOrder(catalog.sizes);
  const error = errorAt("section:variants");

  // A type's chips replace that type's picks and keep the other types'
  const pickValues = (typeValueIds: string[], picked: string[]) =>
    onChange({ optionValueIds: [...form.optionValueIds.filter((id) => !typeValueIds.includes(id)), ...picked] });

  return (
    <section className="editor-section" data-target="section:variants">
      <h3 className="editor-section__title">Variants</h3>
      {types.length === 0 && sizes.length === 0 ? (
        <EmptyState compact title="No sizes or variants yet." description="Add them in Catalog setup, then pick them here." />
      ) : (
        <>
          {types.map((type) => {
            const ids = type.values.map((v) => v.id);
            return (
              <Field key={type.id} label={type.name}>
                <ChipGroup
                  ariaLabel={type.name}
                  value={form.optionValueIds.filter((id) => ids.includes(id))}
                  onChange={(picked) => pickValues(ids, picked)}
                  options={byOrder(type.values).map((v) => ({
                    value: v.id,
                    label: v.label,
                    swatch: type.hasSwatch ? v.swatchHex : undefined,
                  }))}
                />
              </Field>
            );
          })}
          {sizes.length > 0 && (
            <Field label="Sizes">
              <ChipGroup
                ariaLabel="Sizes"
                value={form.sizeIds}
                onChange={(sizeIds) => onChange({ sizeIds })}
                options={sizes.map((s) => ({ value: s.id, label: s.label }))}
              />
            </Field>
          )}
          {combos.tooMany ? (
            <Alert tone="danger">
              {TOO_MANY_COMBINATIONS} ({combos.count} combinations; the limit is {MAX_SKUS}).
            </Alert>
          ) : (
            <p className="editor-hint">
              {combos.skus.length} {combos.skus.length === 1 ? "combination" : "combinations"}
            </p>
          )}
          {error && !combos.tooMany && <Alert tone="danger">{error}</Alert>}
        </>
      )}
    </section>
  );
}

export default VariantsSection;
